import { DataStoreCell, DataStoreField } from "@/datastore/types";

export interface CellResult {
  ok: boolean;
  value?: DataStoreCell;
  error?: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TRUE_VALUES = new Set(["true", "yes", "1"]);
const FALSE_VALUES = new Set(["false", "no", "0"]);

/**
 * Validate and coerce one cell against its field definition.
 * Single source of truth for inline edit, the row form, and the import
 * dry run — mirrors the server-side field-type registry contract.
 */
export function validateCell(raw: unknown, field: DataStoreField): CellResult {
  const text = raw == null ? "" : String(raw).trim();

  if (text === "") {
    if (field.required) {
      return { ok: false, error: `${field.name} is required` };
    }
    return { ok: true, value: null };
  }

  switch (field.type) {
    case "TEXT":
    case "LONG_TEXT": {
      const max = field.settings?.maxLength;
      if (max && text.length > max) {
        return {
          ok: false,
          error: `${field.name} exceeds the maximum length of ${max}`,
        };
      }
      return { ok: true, value: String(raw) };
    }
    case "NUMBER": {
      const n = Number(text);
      if (Number.isNaN(n)) {
        return { ok: false, error: `"${text}" is not a valid number` };
      }
      return { ok: true, value: n };
    }
    case "BOOLEAN": {
      if (typeof raw === "boolean") return { ok: true, value: raw };
      const low = text.toLowerCase();
      if (TRUE_VALUES.has(low)) return { ok: true, value: true };
      if (FALSE_VALUES.has(low)) return { ok: true, value: false };
      return { ok: false, error: `"${text}" is not a valid boolean` };
    }
    case "DATE": {
      if (!DATE_RE.test(text) || Number.isNaN(Date.parse(text))) {
        return { ok: false, error: `"${text}" is not a valid date` };
      }
      return { ok: true, value: text };
    }
    case "DATETIME": {
      if (Number.isNaN(Date.parse(text))) {
        return { ok: false, error: `"${text}" is not a valid date-time` };
      }
      return { ok: true, value: text };
    }
    case "SINGLE_SELECT": {
      const options = field.settings?.options ?? [];
      if (!options.includes(text)) {
        return {
          ok: false,
          error: `"${text}" is not one of the options for ${field.name}`,
        };
      }
      return { ok: true, value: text };
    }
    case "UUID": {
      if (!UUID_RE.test(text)) {
        return { ok: false, error: `"${text}" is not a valid UUID` };
      }
      return { ok: true, value: text.toLowerCase() };
    }
    default:
      return { ok: true, value: String(raw) };
  }
}
